import { useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { Button } from '@/components/shadcn/button';
import { Input } from '@/components/shadcn/input';
import { Card } from '@/components/shadcn/card';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/shadcn/sheet';
import type { MabarParticipant } from '@/services/communityService';
import { countPlayerAppearances } from './pickleballListUtils';
import type { PickleballSessionListItem } from '@/services/pickleballService';

interface PickleballManualGameSheetProps {
  open: boolean;
  participants: MabarParticipant[];
  sessions: PickleballSessionListItem[];
  saving: boolean;
  onClose: () => void;
  onSave: (payload: { playerFormat: 'singles' | 'doubles'; sideA: number[]; sideB: number[] }) => void;
}

type SlotKey = { side: 'a' | 'b'; index: number };

function emptySlots(count: number): number[] {
  return Array.from({ length: count }, () => 0);
}

export default function PickleballManualGameSheet({
  open,
  participants,
  sessions,
  saving,
  onClose,
  onSave,
}: PickleballManualGameSheetProps) {
  const [playerFormat, setPlayerFormat] = useState<'singles' | 'doubles'>('singles');
  const [sideA, setSideA] = useState<number[]>(emptySlots(1));
  const [sideB, setSideB] = useState<number[]>(emptySlots(1));
  const [picker, setPicker] = useState<SlotKey | null>(null);
  const [query, setQuery] = useState('');
  const perSide = playerFormat === 'doubles' ? 2 : 1;
  const lineupIds = useMemo(() => new Set([...sideA, ...sideB].filter(Boolean)), [sideA, sideB]);
  const ready = sideA.every(Boolean) && sideB.every(Boolean);

  useEffect(() => {
    if (!open) return;
    setPlayerFormat('singles');
    setSideA(emptySlots(1));
    setSideB(emptySlots(1));
    setPicker(null);
    setQuery('');
  }, [open]);

  const changeFormat = (next: 'singles' | 'doubles') => {
    setPlayerFormat(next);
    setSideA(emptySlots(next === 'doubles' ? 2 : 1));
    setSideB(emptySlots(next === 'doubles' ? 2 : 1));
  };

  const replaceSlot = (slot: SlotKey, participantId: number) => {
    const setter = slot.side === 'a' ? setSideA : setSideB;
    const current = slot.side === 'a' ? sideA : sideB;
    const next = [...current];
    next[slot.index] = participantId;
    setter(next);
    setPicker(null);
    setQuery('');
  };

  const candidates = participants.filter((p) => {
    const current = picker && (picker.side === 'a' ? sideA[picker.index] : sideB[picker.index]);
    if (lineupIds.has(p.id) && current !== p.id) return false;
    if (query && !p.name.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  const renderSlot = (id: number, label: string, slot: SlotKey) => {
    const name = participants.find((p) => p.id === id)?.name || 'Pilih pemain';
    return (
      <Card
        key={`${slot.side}-${slot.index}`}
        className="flex flex-row items-center justify-between rounded-2xl border-gray-100 px-3 py-3 shadow-none"
      >
        <div>
          <p className="text-sm font-extrabold text-[#0E1726]">{id ? name : 'Kosong'}</p>
          <p className="text-xs text-gray-400">{label}</p>
        </div>
        <Button
          type="button"
          variant="ghost"
          className="h-auto gap-1 px-0 text-sm font-semibold text-[#FC480D] hover:bg-transparent"
          onClick={() => setPicker(slot)}
        >
          <Icon icon="mdi:account-plus-outline" className="h-4 w-4" />
          Pilih
        </Button>
      </Card>
    );
  };

  return (
    <>
      <Sheet open={open && picker == null} onOpenChange={(next) => !next && onClose()}>
        <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto rounded-t-3xl">
          <SheetHeader>
            <SheetTitle>Tambah Game Pickleball</SheetTitle>
            <SheetDescription>Default rally 21, 3 set. Pilih format dan pemain confirmed.</SheetDescription>
          </SheetHeader>
          <div className="mt-4 grid grid-cols-2 gap-2">
            {(['singles', 'doubles'] as const).map((fmt) => (
              <Button
                key={fmt}
                type="button"
                variant="outline"
                onClick={() => changeFormat(fmt)}
                className={`h-11 rounded-full ${
                  playerFormat === fmt
                    ? 'border-[#FC480D] bg-[#FFF8F5] text-[#FC480D]'
                    : 'border-gray-200 text-[#0E1726]'
                }`}
              >
                {fmt === 'singles' ? 'Tunggal' : 'Ganda'}
              </Button>
            ))}
          </div>
          <div className="mt-4 space-y-2">
            {sideA.map((id, index) => renderSlot(id, `Tim A · slot ${index + 1}`, { side: 'a', index }))}
            {sideB.map((id, index) => renderSlot(id, `Tim B · slot ${index + 1}`, { side: 'b', index }))}
          </div>
          {participants.length < perSide * 2 && (
            <p className="mt-3 text-sm text-amber-700">
              Minimal {perSide * 2} peserta confirmed diperlukan untuk format ini.
            </p>
          )}
          <Button
            type="button"
            disabled={saving || !ready}
            className="mt-5 h-12 w-full rounded-full bg-[#FC480D] font-bold text-white hover:bg-[#D83B08]"
            onClick={() => onSave({ playerFormat, sideA, sideB })}
          >
            Simpan Game
          </Button>
        </SheetContent>
      </Sheet>
      <Sheet open={picker != null} onOpenChange={(next) => !next && setPicker(null)}>
        <SheetContent side="bottom" className="rounded-t-3xl">
          <SheetHeader>
            <SheetTitle>Pilih Pemain</SheetTitle>
          </SheetHeader>
          <div className="relative mt-3">
            <Icon icon="mdi:magnify" className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cari nama pemain"
              className="h-11 rounded-full pl-9"
            />
          </div>
          <div className="mt-3 max-h-80 space-y-2 overflow-y-auto">
            {candidates.map((player) => (
              <Button
                key={player.id}
                type="button"
                variant="outline"
                className="h-auto w-full justify-between rounded-2xl border-gray-100 px-3 py-3"
                onClick={() => picker && replaceSlot(picker, player.id)}
              >
                <span className="text-left">
                  <span className="block text-sm font-extrabold text-[#0E1726]">{player.name}</span>
                  <span className="block text-xs font-normal text-gray-400">
                    {countPlayerAppearances(sessions, player.id)}x main
                  </span>
                </span>
                <Icon icon="mdi:plus-circle-outline" className="h-5 w-5 text-[#FC480D]" />
              </Button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
